import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { selectColumn } from '@/components/dataTable/lib/selectColumn';
import { useDynamicLocale } from '@/hoslog-intl';
import { CheckIcon, CrossIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import { AffiliateWithdrawStatus, MonthlyReportData } from '@/services/payment/payment.types';
import { MONTHLY_REPORT_LABELS } from '../constants';

const statusClasses: Record<AffiliateWithdrawStatus, string> = {
  [AffiliateWithdrawStatus.PENDING]: 'text-warning',
  [AffiliateWithdrawStatus.APPROVED]: 'text-success',
  [AffiliateWithdrawStatus.REJECTED]: 'text-destructive-text',
};

export function getColumns(t: ReturnType<typeof useDynamicLocale>['t']): ColumnDef<MonthlyReportData>[] {
  const statusLabels: Record<AffiliateWithdrawStatus, string> = {
    [AffiliateWithdrawStatus.PENDING]: t('common.page.table.pending'),
    [AffiliateWithdrawStatus.APPROVED]: t('common.page.table.approved'),
    [AffiliateWithdrawStatus.REJECTED]: t(MONTHLY_REPORT_LABELS.rejected),
  };

  return [
    selectColumn as ColumnDef<MonthlyReportData>,
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'requestId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestId')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('requestId')}</div>,
      meta: {
        label: t('common.page.tableColumn.requestId'),
      },
      enableHiding: false,
    },
    {
      // The toolbar's date filter binds to the column with id `createdDate`.
      id: 'createdDate',
      accessorKey: 'requestDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t(MONTHLY_REPORT_LABELS.requestedDate)} />,
      cell: ({ row }) => <div>{formatDate(row.original.requestDate, 'YYYY/MM/DD HH:mm:ss')}</div>,
      meta: {
        label: t(MONTHLY_REPORT_LABELS.requestedDate),
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'bankType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t(MONTHLY_REPORT_LABELS.bankType)} />,
      cell: ({ row }) => <div>{row.getValue('bankType')}</div>,
      meta: {
        label: t(MONTHLY_REPORT_LABELS.bankType),
      },
      enableHiding: true,
    },
    {
      accessorKey: 'bankName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.bankInfo')} />,
      cell: ({ row }) => <div>{row.getValue('bankName')}</div>,
      meta: {
        label: t('common.page.tableColumn.bankInfo'),
      },
      enableHiding: true,
    },
    {
      accessorKey: 'accountSymbol',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.accountSymbol')} />
      ),
      cell: ({ row }) => <div>{row.original.accountSymbol || '-'}</div>,
      meta: {
        label: t('common.page.tableColumn.accountSymbol'),
      },
      enableHiding: true,
    },
    {
      accessorKey: 'accountNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.accountNumber')} />
      ),
      cell: ({ row }) => <div>{row.getValue('accountNumber')}</div>,
      meta: {
        label: t('common.page.tableColumn.accountNumber'),
      },
      enableHiding: true,
    },
    {
      accessorKey: 'accountName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t(MONTHLY_REPORT_LABELS.accountHolderName)} />
      ),
      cell: ({ row }) => <div>{row.getValue('accountName')}</div>,
      meta: {
        label: t(MONTHLY_REPORT_LABELS.accountHolderName),
      },
      enableHiding: true,
    },
    {
      accessorKey: 'requestedAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t(MONTHLY_REPORT_LABELS.requestedAmount)} />
      ),
      cell: ({ row }) => <div className="whitespace-nowrap">{formatAmount(row.original.requestedAmount)}</div>,
      meta: {
        label: t(MONTHLY_REPORT_LABELS.requestedAmount),
        align: 'right',
      },
      enableHiding: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ cell }) => {
        const status = cell.getValue<MonthlyReportData['status']>();
        return (
          <span className={`whitespace-nowrap text-xs font-medium ${statusClasses[status]}`}>
            {statusLabels[status] ?? status}
          </span>
        );
      },
      meta: {
        // The toolbar renders a status dropdown for columns with options and this label.
        label: t('common.page.tableColumn.status'),
        options: [
          { label: statusLabels[AffiliateWithdrawStatus.PENDING], value: AffiliateWithdrawStatus.PENDING },
          { label: statusLabels[AffiliateWithdrawStatus.APPROVED], value: AffiliateWithdrawStatus.APPROVED },
          { label: statusLabels[AffiliateWithdrawStatus.REJECTED], value: AffiliateWithdrawStatus.REJECTED },
        ],
      },
      enableHiding: true,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'remarks',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.remarks')} />,
      cell: ({ row }) => <div>{row.original.remarks || '-'}</div>,
      meta: {
        label: t('common.page.tableColumn.remarks'),
      },
      enableHiding: true,
    },
    {
      id: 'actions',
      accessorKey: 'Actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      cell: ({ row }) => (
        <>
          {row.original.status === AffiliateWithdrawStatus.PENDING ? (
            <div className="flex gap-4">
              <CheckIcon className="text-success cursor-pointer" />
              <CrossIcon className="text-destructive-text cursor-pointer" />
            </div>
          ) : (
            ''
          )}
        </>
      ),
      size: 40,
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
